import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { gql, useQuery } from '@apollo/client';

import { useSeason } from '../SeasonState';
import { Checkbox, AmountInput, TextInput, Text } from '../uiold';
import { LoadingIndicator, ApolloError, VStack, HStack } from '../../core-ui';
import VendorInvoiceSummary from './VendorInvoiceSummary';
import { Billing, Payment, LineItem } from '../../types/shared/Vendor';
import type {
  GetVendorInvoicePreview,
  GetVendorInvoicePreviewVariables,
} from './__generated__/GetVendorInvoicePreview';

const GET_VENDOR_INVOICE_PREVIEW = gql`
  query GetVendorInvoicePreview(
    $seasonId: String!
    $vendorId: String!
    $selectedDates: [String!]!
    $includeSalesData: Boolean!
  ) {
    vendor(id: $vendorId) {
      id
      name
      billing
      payments
      invoicePreview(
        selectedDates: $selectedDates
        includeSalesData: $includeSalesData
      ) {
        id
        invoicedLineItems
        totalPrice
        taxAmount
        adjustmentAmount
        adjustmentReason
        taxPercent
        isAdjustmentTaxable
        createdAt
      }
      createdAt
    }
    season(id: $seasonId) {
      id
      markets {
        id
        name
        dates
        numSpots
      }
      addons {
        id
        name
        defaultPrice
        createdAt
        updatedAt
      }
    }
  }
`;

type Props = {
  vendorId: string;
  selectedDates: Set<string>;
  currency: string;
  initialAdjustment: Adjustment | undefined;
  onAdjustmentChange: (adjustment: Adjustment) => void;
  includeSalesData: boolean;
};

export type Adjustment = {
  amount: number;
  reason: string;
  taxable: boolean;
};

function getEmptyAdjustment(): Adjustment {
  return { amount: 0, reason: '', taxable: false };
}

export function InvoiceDetailView(props: Props) {
  const {
    vendorId,
    selectedDates,
    currency,
    initialAdjustment,
    onAdjustmentChange,
    includeSalesData,
  } = props;

  const [adjustment, setAdjustment] = useState<Adjustment>(() => {
    return initialAdjustment ?? getEmptyAdjustment();
  });

  // Here we'll set the state and notify the parent component
  const updateAdjustment = (newAdjustment: Adjustment) => {
    setAdjustment(newAdjustment);
    onAdjustmentChange(newAdjustment);
  };

  const { season } = useSeason();

  const { data, loading, error } = useQuery<
    GetVendorInvoicePreview,
    GetVendorInvoicePreviewVariables
  >(GET_VENDOR_INVOICE_PREVIEW, {
    variables: {
      seasonId: season?.id ?? '',
      vendorId,
      selectedDates: Array.from(selectedDates),
      includeSalesData,
    },
    fetchPolicy: 'cache-and-network',
  });

  const normalized = useMemo(() => {
    if (!data || !data.vendor || !data.season) {
      return null;
    }

    const {
      id,
      name,
      billing,
      payments,
      invoicePreview,
      createdAt,
    } = data.vendor;
    const vendor = {
      id,
      name,
      billing: billing ? (JSON.parse(billing) as Billing) : undefined,
      payments: payments.map(
        (stringified) => JSON.parse(stringified) as Payment,
      ),
      created_at: createdAt,
    };

    const { invoicedLineItems, createdAt: _, ...other } = invoicePreview;
    const invoice = {
      ...other,
      vendorId: vendor.id,
      invoicedLineItems: invoicedLineItems.map(
        (lineItem) => JSON.parse(lineItem) as LineItem,
      ),
      created_at: invoicePreview.createdAt,
    };

    const markets = data.season.markets.map((market) => {
      const { id, name, dates, numSpots } = market;
      return { id, name, dates, num_spots: numSpots };
    });

    const addons = new Map(
      data.season.addons.map((addon) => {
        const { id, name, defaultPrice, createdAt, updatedAt } = addon;
        const normalizedAddon = {
          id,
          name,
          defaultPrice,
          created_at: createdAt,
          modified_at: updatedAt,
        };
        return [id, normalizedAddon] as const;
      }),
    );

    return { vendor, invoice, markets, addons };
  }, [data]);

  if (error) {
    return <ApolloError error={error} />;
  }

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (!normalized) {
    return <p>{t('Not found')}</p>;
  }

  const { vendor, invoice, markets, addons } = normalized;

  // Re-compute the invoice including adjustment amounts
  const adjustmentAmount = adjustment.amount ?? 0;
  const adjustmentTaxAmount = adjustment.taxable
    ? adjustmentAmount * invoice.taxPercent
    : 0;
  const vendorInvoice = {
    ...invoice,
    taxAmount: invoice.taxAmount + adjustmentTaxAmount,
    totalPrice: invoice.totalPrice + adjustmentAmount + adjustmentTaxAmount,
    adjustmentAmount: adjustment.amount,
    adjustmentReason: adjustment.reason,
    isAdjustmentTaxable: adjustment.taxable,
  };

  return (
    <VStack flex={1} align="stretch" spacing={0}>
      <VendorInvoiceSummary
        key={vendor.id}
        vendor={vendor}
        markets={markets}
        vendorInvoice={vendorInvoice}
        currency={currency}
        addons={addons}
        containerProps={{ flex: 1 }}
      />
      <VStack
        align="stretch"
        position="sticky"
        bottom={0}
        paddingBottom={2}
        backgroundColor="white"
        boxShadow="0 -1px var(--chakra-colors-gray-100)"
      >
        <HStack align="center">
          <View style={styles.inputAmount}>
            <Text style={styles.label}>{t('Adjustment Amount')}</Text>
            <AmountInput
              amount={adjustment.amount}
              onChange={(amount) => {
                updateAdjustment({ ...adjustment, amount });
              }}
            />
          </View>
          <View style={styles.adjustmentReason}>
            <Text style={styles.label}>{t('Adjustment Reason')}</Text>
            <TextInput
              value={adjustment.reason}
              onChangeText={(reason) => {
                updateAdjustment({ ...adjustment, reason });
              }}
            />
          </View>
          <View style={styles.adjustmentTaxable}>
            <Text style={styles.label}>{t('Taxable?')}</Text>
            <Checkbox
              isChecked={adjustment.taxable}
              onChange={(taxable) => {
                updateAdjustment({ ...adjustment, taxable });
              }}
              style={{ justifyContent: 'center', paddingRight: '0px' }}
              textStyle={{ display: 'none' }}
            />
          </View>
          <View style={styles.adjustmentTaxRate}>
            <Text style={styles.label}>{t('Tax %')}</Text>
            <Text>{`${Math.round(invoice.taxPercent * 100)}%`}</Text>
          </View>
        </HStack>
      </VStack>
    </VStack>
  );
}

let styles = StyleSheet.create({
  inputAmount: {
    width: '30%',
    paddingRight: 20,
  },
  adjustmentReason: {
    marginRight: 20,
    width: '40%',
  },
  adjustmentTaxable: {
    marginRight: 20,
    width: '10%',
    alignItems: 'center',
  },
  adjustmentTaxRate: {
    marginRight: 20,
    width: '10%',
    alignItems: 'flex-end',
  },
  label: { marginVertical: 4 },
});
